
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import IndividualAgentSalesMetricTableFilterable from '@/components/reports-v3/components/filterables/IndividualAgentSalesMetricTableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {COL, getTableName} from '@/worker/db/sqlHelpers';
import {PermissionsGroup, IndividualAgentCustomerSalesMetrics} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import DateFilter from '@/components/reports-v3/components/filters/DateFilter.vue';
import moment from 'moment';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import {roundToFixed} from '@/util/number';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        IndividualAgentSalesMetricTableFilterable,
        DateFilter,
        SelectFilter,
        IncludeTaxToggle,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class AgentSalesMetricsTable extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'quotations'];
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'compareDate1'
        | 'compareDate2'
    > = [];

    public year1Result = {} as IndividualAgentCustomerSalesMetrics;
    public year2Result = {} as IndividualAgentCustomerSalesMetrics;
    public year3Result = {} as IndividualAgentCustomerSalesMetrics;
    public year4Result = {} as IndividualAgentCustomerSalesMetrics;

    public selectedComparedYear: number = this.compareYearList[0];

    public get compareYearList() {
        const currentYear = moment().year();

        const list: number[] = [];

        for (let i = 1; i < 10; i++) {
            const year = currentYear - i;
            list.push(year);
        }

        return list;
    }

    public get ytdRange(): [number, number] {
        return [
            moment(this.selectedAsOfDate).startOf('year').valueOf(),
            moment(this.selectedAsOfDate).endOf('month').valueOf(),
        ];
    }

    public get numDecimal() {
        return gdbx.numDecimal;
    }

    public get includeTax() {
        return settingx.now.includeTax;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get expensiveHook() {
        const {
            selectedAgents,
            selectedAsOfDate,
            selectedComparedYear,
            ytdRange,
            includeTax,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedAgents,
            selectedAsOfDate,
            selectedComparedYear,
            ytdRange,
            includeTax,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        const mmt = moment(this.selectedAsOfDate);
        const currentMonth = mmt.month() + 1;
        const currentYear = mmt.year();

        const queryHead = await this._ensureTablesLoaded(
            ['sales', 'creditNotes', 'customerTrades'] as any[],
        );

        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');

        // Determine docTypes based on Debit Note toggle
        const salesDocTypes = this.includeDebitNote
            ? `('IV', 'CS', 'DN')` : `('IV', 'CS')`;
        const tradeDocTypes = this.includeDebitNote
            ? `('IV', 'DN')` : `('IV')`;

        // Amount/profit SQL expressions based on Tax toggle
        const slAmtExpr = this.includeTax
            ? '(TRY_CAST("AMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("TAX" AS DOUBLE), 0))'
            : 'TRY_CAST("AMOUNT" AS DOUBLE)';
        const slProfExpr = this.includeTax
            ? '(TRY_CAST("AMOUNT" AS DOUBLE) + TRY_CAST("TAX" AS DOUBLE) - TRY_CAST("COST" AS DOUBLE))'
            : 'TRY_CAST("PROFIT" AS DOUBLE)';
        const arAmtExpr = this.includeTax
            ? '(TRY_CAST("LOCALAMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("LOCALTAXAMT" AS DOUBLE), 0))'
            : 'TRY_CAST("LOCALAMOUNT" AS DOUBLE)';

        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const agentFilter = this.selectedAgents.length > 0
            ? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',')})`
            : '';

        // Define years [year1, year2, year3, year4]
        const years = [
            this.selectedComparedYear,
            this.selectedComparedYear - 1,
            this.selectedComparedYear - 2,
            this.selectedComparedYear - 3,
        ];

        const getDateFilter = (year: number) => {
            const start = moment().year(year).startOf('year').valueOf();
            const end = moment().year(year).endOf('year').valueOf();
            return `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${start} AND ${end}`;
        };

        this.generateLoadingText(0.2, 0.5);

        // ********
        // Ranking (uses SL data for all agents)
        // ********
        const rankingQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT ${COL.AGENT} as agent,
                    SUM(CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${slAmtExpr} ELSE 0 END) as salesAmt,
                    SUM(${slAmtExpr}) as cnAmt
                FROM ${salesTable}
                WHERE ${df}
                GROUP BY ${COL.AGENT}
            `);
        });
        const cnRankingQueries = this.includeCreditNote
            ? years.map((year) => {
                const df = getDateFilter(year);
                return queryHead.rawQuery(`
                    SELECT ${COL.AGENT} as agent,
                        SUM(${slAmtExpr}) as cnAmt
                    FROM ${cnTable}
                    WHERE ${df}
                    GROUP BY ${COL.AGENT}
                `);
            })
            : years.map(() => Promise.resolve([]));

        const [rankResults, cnRankResults] = await Promise.all([
            Promise.all(rankingQueries),
            Promise.all(cnRankingQueries),
        ]);

        const salesRankings: number[] = years.map((year, i) => {
            const cnMap = new Map<string, number>();
            if (this.includeCreditNote) {
                for (const r of cnRankResults[i]) {
                    cnMap.set(String(r.agent), Number(r.cnAmt) || 0);
                }
            }
            const netSales = rankResults[i].map((r) => ({
                agent: String(r.agent),
                net: (Number(r.salesAmt) || 0)
                    - (this.includeCreditNote
                        ? (cnMap.get(String(r.agent)) || 0) : 0),
            }));
            netSales.sort((a, b) => b.net - a.net);
            const idx = netSales.findIndex(
                (dd) => dd.agent === this.selectedAgents[0],
            );
            return idx + 1;
        });

        this.generateLoadingText(0.5, 0.8);

        // ********
        // SL Mode queries (per year, for selected agent + all agents)
        // ********
        const slQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT
                    COUNT(DISTINCT CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${COL.DOCNO} END) as docCount,
                    COUNT(DISTINCT CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${COL.DOCDATE} END) as dateCount,
                    SUM(CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${slAmtExpr} ELSE 0 END) as salesAmt,
                    SUM(CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${slProfExpr} ELSE 0 END) as salesProf
                FROM ${salesTable}
                WHERE ${df}${agentFilter}
            `);
        });
        const slAllQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT
                    COUNT(DISTINCT CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${COL.DOCNO} END) as docCount,
                    SUM(CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${slAmtExpr} ELSE 0 END) as salesAmt,
                    SUM(CASE WHEN ${COL.DOCTYPE} IN ${salesDocTypes}
                        THEN ${slProfExpr} ELSE 0 END) as salesProf
                FROM ${salesTable}
                WHERE ${df}
            `);
        });
        const cnQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT
                    COUNT(DISTINCT ${COL.DOCNO}) as docCount,
                    SUM(${slAmtExpr}) as cnAmt,
                    SUM(${slProfExpr}) as cnProf
                FROM ${cnTable}
                WHERE ${df}${agentFilter}
            `);
        });
        const cnAllQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT
                    SUM(${slAmtExpr}) as cnAmt,
                    SUM(${slProfExpr}) as cnProf
                FROM ${cnTable}
                WHERE ${df}
            `);
        });

        // ********
        // AR Mode queries
        // ********
        const arQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT
                    SUM(CASE WHEN ${COL.DOCTYPE} IN ${tradeDocTypes}
                        THEN ${arAmtExpr} ELSE 0 END) as salesAmt,
                    SUM(CASE WHEN ${COL.DOCTYPE} = 'CN'
                        THEN ${arAmtExpr} ELSE 0 END) as cnAmt,
                    COUNT(DISTINCT CASE WHEN ${COL.DOCTYPE} IN ${tradeDocTypes}
                        THEN ${COL.DOCDATE} END) as dateCount
                FROM ${arTable}
                WHERE ${df}${agentFilter}
            `);
        });
        const arAllQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT
                    SUM(CASE WHEN ${COL.DOCTYPE} IN ${tradeDocTypes}
                        THEN ${arAmtExpr} ELSE 0 END) as salesAmt,
                    SUM(CASE WHEN ${COL.DOCTYPE} = 'CN'
                        THEN ${arAmtExpr} ELSE 0 END) as cnAmt
                FROM ${arTable}
                WHERE ${df}
            `);
        });

        // AR DocRefs queries (for case counting)
        const arRefsQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT ${COL.DOCTYPE} as docType,
                    COUNT(DISTINCT ref) as refCount
                FROM (
                    SELECT ${COL.DOCTYPE},
                        UNNEST(string_split("DOCREFS", '|')) as ref
                    FROM ${arTable}
                    WHERE ${df}${agentFilter}
                ) sub
                WHERE ref IS NOT NULL AND TRIM(ref) != ''
                GROUP BY ${COL.DOCTYPE}
            `);
        });
        const arAllRefsQueries = years.map((year) => {
            const df = getDateFilter(year);
            return queryHead.rawQuery(`
                SELECT ${COL.DOCTYPE} as docType,
                    COUNT(DISTINCT ref) as refCount
                FROM (
                    SELECT ${COL.DOCTYPE},
                        UNNEST(string_split("DOCREFS", '|')) as ref
                    FROM ${arTable}
                    WHERE ${df}
                ) sub
                WHERE ref IS NOT NULL AND TRIM(ref) != ''
                GROUP BY ${COL.DOCTYPE}
            `);
        });

        const [
            slResults, slAllResults, cnResults, cnAllResults,
            arResults, arAllResults, arRefsResults, arAllRefsResults,
        ] = await Promise.all([
            Promise.all(slQueries),
            Promise.all(slAllQueries),
            Promise.all(cnQueries),
            Promise.all(cnAllQueries),
            Promise.all(arQueries),
            Promise.all(arAllQueries),
            Promise.all(arRefsQueries),
            Promise.all(arAllRefsQueries),
        ]);

        this.generateLoadingText(0.8, 0.95);

        const getVal = (rows: any[], key: string) =>
            Number(rows[0] && rows[0][key]) || 0;
        const getRefCount = (rows: any[], docType: string): number => {
            const row = rows.find((r: any) => String(r.docType) === docType);
            return row ? (Number(row.refCount) || 0) : 0;
        };

        // ********
        // Build per-year metrics
        // ********
        const salesAmounts: number[] = [];
        const allSalesAmounts: number[] = [];
        const salesProfits: number[] = [];
        const allSalesProfits: number[] = [];
        const salesDocQtys: number[] = [];
        const allSalesDocQtys: number[] = [];
        const salesDocDateQtys: number[] = [];

        for (let i = 0; i < years.length; i++) {
            const sl = slResults[i];
            const slAll = slAllResults[i];
            const cn = cnResults[i];
            const cnAll = cnAllResults[i];
            const ar = arResults[i];
            const arAll = arAllResults[i];

            // SL amounts
            const slAmt = getVal(sl, 'salesAmt');
            const slProf = getVal(sl, 'salesProf');
            const slCnAmt = this.includeCreditNote ? getVal(cn, 'cnAmt') : 0;
            const slCnProf = this.includeCreditNote ? getVal(cn, 'cnProf') : 0;
            const slAllAmt = getVal(slAll, 'salesAmt');
            const slAllProf = getVal(slAll, 'salesProf');
            const slAllCnAmt = this.includeCreditNote
                ? getVal(cnAll, 'cnAmt') : 0;
            const slAllCnProf = this.includeCreditNote
                ? getVal(cnAll, 'cnProf') : 0;

            // AR amounts
            const arAmt = getVal(ar, 'salesAmt');
            const arCnAmt = this.includeCreditNote
                ? getVal(ar, 'cnAmt') : 0;
            const arAllAmt = getVal(arAll, 'salesAmt');
            const arAllCnAmt = this.includeCreditNote
                ? getVal(arAll, 'cnAmt') : 0;

            if (this.slPhModuleOnly) {
                salesAmounts.push(slAmt - slCnAmt);
                allSalesAmounts.push(slAllAmt - slAllCnAmt);
                salesProfits.push(slProf - slCnProf);
                allSalesProfits.push(slAllProf - slAllCnProf);

                const slDocQty = getVal(sl, 'docCount');
                const cnDocQty = this.includeCreditNote
                    ? getVal(cn, 'docCount') : 0;
                const slAllDocQty = getVal(slAll, 'docCount');
                const cnAllDocQty = this.includeCreditNote
                    ? getVal(cnAll, 'docCount') || 0 : 0;
                salesDocQtys.push(slDocQty - cnDocQty);
                allSalesDocQtys.push(slAllDocQty - cnAllDocQty);
                salesDocDateQtys.push(getVal(sl, 'dateCount'));
            } else {
                salesAmounts.push(arAmt - arCnAmt);
                allSalesAmounts.push(arAllAmt - arAllCnAmt);
                // AR profit = SL profit + (AR amount - SL amount)
                const netSlProf = slProf - slCnProf;
                const netSlAmt = slAmt - slCnAmt;
                const netArAmt = arAmt - arCnAmt;
                salesProfits.push(netSlProf + (netArAmt - netSlAmt));
                const netAllSlProf = slAllProf - slAllCnProf;
                const netAllSlAmt = slAllAmt - slAllCnAmt;
                const netAllArAmt = arAllAmt - arAllCnAmt;
                allSalesProfits.push(
                    netAllSlProf + (netAllArAmt - netAllSlAmt),
                );

                // AR cases from docRefs
                const ivRefCnt = getRefCount(arRefsResults[i], 'IV');
                const dnRefCnt = this.includeDebitNote
                    ? getRefCount(arRefsResults[i], 'DN') : 0;
                const cnRefCnt = this.includeCreditNote
                    ? getRefCount(arRefsResults[i], 'CN') : 0;
                salesDocQtys.push(ivRefCnt + dnRefCnt - cnRefCnt);

                const allIvRefCnt = getRefCount(arAllRefsResults[i], 'IV');
                const allDnRefCnt = this.includeDebitNote
                    ? getRefCount(arAllRefsResults[i], 'DN') : 0;
                const allCnRefCnt = this.includeCreditNote
                    ? getRefCount(arAllRefsResults[i], 'CN') : 0;
                allSalesDocQtys.push(
                    allIvRefCnt + allDnRefCnt - allCnRefCnt,
                );

                salesDocDateQtys.push(getVal(ar, 'dateCount'));
            }
        }

        this.generateLoadingText(1);

        // ********
        // Derived Metrics
        // ********
        const salesMargins = salesAmounts.map((amount, i) =>
            amount !== 0
                ? roundToFixed((salesProfits[i] / amount) * 100, this.numDecimal)
                : 0,
        );
        const revenueContributions = allSalesAmounts.map((allAmount, i) =>
            allAmount !== 0
                ? roundToFixed((salesAmounts[i] / allAmount) * 100, this.numDecimal)
                : 0,
        );
        const grossProfitContributions = allSalesProfits.map((allProfit, i) =>
            allProfit !== 0
                ? roundToFixed((salesProfits[i] / allProfit) * 100, this.numDecimal)
                : 0,
        );
        const caseContributions = allSalesDocQtys.map((allQty, i) =>
            allQty !== 0
                ? roundToFixed((salesDocQtys[i] / allQty) * 100, this.numDecimal)
                : 0,
        );
        const avgSalesPerCases = salesDocQtys.map((qty, i) =>
            qty !== 0 ? salesAmounts[i] / qty : 0,
        );
        const avgSalesCases = years.map((year, i) =>
            year === currentYear
                ? salesDocQtys[i] / currentMonth
                : salesDocQtys[i] / 12,
        );
        const avgSales = years.map((year, i) =>
            year === currentYear
                ? salesAmounts[i] / currentMonth
                : salesAmounts[i] / 12,
        );
        const avgSalesCycles = years.map((year, i) => {
            if (salesDocDateQtys[i] === 0) { return 0; }
            const daysInYear = year === currentYear
                ? moment(this.ytdRange[1]).diff(
                    moment(this.ytdRange[0]), 'days',
                )
                : moment().endOf('year').year(year).dayOfYear();
            return daysInYear / salesDocDateQtys[i];
        });

        // ********
        // Assign Results
        // ********
        const results = years.map((year, i) => ({
            year,
            salesRanking: salesRankings[i],
            salesMargin: salesMargins[i],
            revenueContribution: revenueContributions[i],
            grossProfitContribution: grossProfitContributions[i],
            caseContribution: caseContributions[i],
            salesAmount: salesAmounts[i],
            salesDocQty: salesDocQtys[i],
            averageSalesPerCase: avgSalesPerCases[i],
            averageSalesCase: avgSalesCases[i],
            averageSales: avgSales[i],
            averageSalesCycle: avgSalesCycles[i],
        }));

        [this.year1Result, this.year2Result, this.year3Result, this.year4Result] = results;

        this.saveHistory(
            'year1Result',
            'year2Result',
            'year3Result',
            'year4Result',
        );
    }

    public onHovering(value: boolean, filterId: string) {
        this.$emit('hovering', value, filterId);
    }

    public onEditing(value: boolean, filterId: string) {
        this.$emit('editing', value, filterId);
    }
}
